/**
 * Related Products Section v1.0
 * Handles fetching and displaying product recommendations using IntersectionObserver.
 */

class RelatedProducts {
  constructor(element) {
    if (!element) {
      console.error('RelatedProducts: No element provided to constructor.');
      return;
    }
    this.container = element;
    this.url = this.container.dataset.url;
    this.sectionId = this.container.dataset.sectionId;

    if (!this.url) {
      console.error(`RelatedProducts (${this.sectionId}): Missing data-url attribute.`);
      this.showError('Configuration error: Missing data-url');
      return;
    }

    // Use IntersectionObserver to load only when visible
    const observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      rootMargin: '0px 0px 200px 0px', // Load 200px before entering viewport
    });
    observer.observe(this.container);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;

    // Stop observing once it's intersecting
    observer.unobserve(this.container);

    this.fetchRecommendations();
  }

  fetchRecommendations() {
    fetch(this.url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        this.renderRecommendations(text);
      })
      .catch((error) => {
        console.error(`RelatedProducts (${this.sectionId}): Error fetching recommendations -`, error);
        this.showError('Could not load recommendations');
      });
  }

  renderRecommendations(htmlContent) {
    // Check if the fetched text is empty or only whitespace
    if (!htmlContent || htmlContent.trim().length === 0) {
      this.showEmpty();
      return;
    }

    // Parse the fetched HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Find the actual recommendations content within the parsed HTML
    // This could be the slider or the empty state message
    const recommendationsContent = tempDiv.querySelector('mx-slider, div.empty-recommendations');

    if (recommendationsContent) {
      // Replace the placeholder content with the extracted slider
      this.container.innerHTML = recommendationsContent.outerHTML;

      // Now implement our own simple slider functionality
      const slider = this.container.querySelector('mx-slider');
      if (slider) {
        const sliderTrack = slider.querySelector('[data-role="slider"]');
        const navDots = slider.querySelectorAll('[data-role="navigation"] .mx-bullet');
        const prevButton = slider.querySelector('[data-role="previous"]');
        const nextButton = slider.querySelector('[data-role="next"]');

        // Check if we're inside mx-section context
        const isMxSectionContext = slider.closest('.mx-section') !== null;

        if (sliderTrack && navDots.length) {
          // Set up slider CSS
          sliderTrack.style.display = 'flex';
          sliderTrack.style.transition = 'transform 0.5s ease';

          // Calculate slide width and gap
          const slides = Array.from(sliderTrack.children);
          const slideWidth = 100 / slides.length;

          // Set up slides with responsive sizing
          slides.forEach((slide) => {
            slide.style.flex = `0 0 auto`;
            slide.style.width = `var(--slide-min-inline-size, ${slideWidth}%)`;
          });

          // --- Define helper functions first ---
          let currentIndex = 0; // Initialize currentIndex here too

          const initialIndexMatch = slider.style.cssText.match(/--initial-slide-index:\s*(\d+)/);

          if (initialIndexMatch && initialIndexMatch[1]) {
            currentIndex = parseInt(initialIndexMatch[1]);
          }

          // Function to go to a specific slide (Sets final state and updates UI)
          const goToSlide = (index) => {
            // Make sure the index is within bounds
            const validIndex = Math.max(0, Math.min(index, slides.length - 1));
            currentIndex = validIndex; // Update the official currentIndex

            // Calculate position based on viewport width
            let position;
            if (window.innerWidth < 640) {
              const mobileSlideWidth = 85; // Match CSS
              position = validIndex * mobileSlideWidth;
            } else {
              position = validIndex * (100 / slides.length);
            }

            sliderTrack.style.transition = 'transform 0.5s ease';
            sliderTrack.style.transform = `translateX(-${position}%)`;

            // The more aggressive approach to update dots
            const navDotsContainer = slider.querySelector('[data-role="navigation"]');
            if (navDotsContainer) {
              // Get all dots by class - this avoids dependency on aria-current existence
              const dots = navDotsContainer.querySelectorAll('.mx-bullet');

              // Set both aria-current and force class-based styling
              dots.forEach((dot, i) => {
                const isActive = i === validIndex;

                // Set attributes used by accessibility and ARIA
                dot.setAttribute('aria-current', isActive ? 'true' : 'false');

                // Add style overrides for both CSS frameworks - try multiple techniques
                if (isActive) {
                  // Force color as inline style with !important
                  dot.style.cssText = 'background-color: #19435a !important; opacity: 1 !important;';

                  // Also try several possible active classes
                  dot.classList.add('active');
                  dot.classList.add('active-dot');
                  dot.classList.add('mx-bullet--active');
                  dot.classList.add('selected');
                  dot.classList.add('mx-bullet--selected');

                  // Remove inactive classes if they exist
                  dot.classList.remove('inactive');
                  dot.classList.remove('mx-bullet--inactive');
                } else {
                  // Reset background for inactive dots
                  dot.style.cssText = '';

                  // Remove all potential active classes
                  dot.classList.remove('active');
                  dot.classList.remove('active-dot');
                  dot.classList.remove('mx-bullet--active');
                  dot.classList.remove('selected');
                  dot.classList.remove('mx-bullet--selected');
                }
              });
            }

            // Update button states (using the reliable re-selecting logic)
            const currentPrevButton = slider.querySelector('[data-role="previous"]');
            const currentNextButton = slider.querySelector('[data-role="next"]');

            if (currentPrevButton) {
              if (validIndex === 0) {
                currentPrevButton.classList.add('disabled');
                currentPrevButton.setAttribute('disabled', 'disabled');
              } else {
                currentPrevButton.classList.remove('disabled');
                currentPrevButton.removeAttribute('disabled');
              }
            }

            if (currentNextButton) {
              if (validIndex === slides.length - 1) {
                currentNextButton.classList.add('disabled');
                currentNextButton.setAttribute('disabled', 'disabled');
              } else {
                currentNextButton.classList.remove('disabled');
                currentNextButton.removeAttribute('disabled');
              }
            }
          };

          // --- Now initialize behaviors that might use the functions ---
          this.initializeDragBehavior(slider, sliderTrack);

          // --- Then set initial state and listeners ---
          // Go to initial slide
          goToSlide(currentIndex);

          // Set up click handlers for dots
          navDots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
              goToSlide(index);
            });
          });

          // If we're in mx-section context, the buttons may already have event handlers
          // that conflict with ours. Let's use a different approach.
          if (isMxSectionContext && prevButton && nextButton) {
            // First, remove any existing click listeners by cloning and replacing the buttons
            const prevParent = prevButton.parentNode;
            const nextParent = nextButton.parentNode;

            const newPrevButton = prevButton.cloneNode(true);
            const newNextButton = nextButton.cloneNode(true);

            prevParent.replaceChild(newPrevButton, prevButton);
            nextParent.replaceChild(newNextButton, nextButton);

            // Add our event listeners to the new buttons
            newPrevButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (currentIndex > 0) {
                goToSlide(currentIndex - 1);
              }
            });

            newNextButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (currentIndex < slides.length - 1) {
                goToSlide(currentIndex + 1);
              }
            });
          } else {
            // Standard event binding for non-mx-section context
            if (prevButton) {
              prevButton.addEventListener('click', () => {
                // Only go to previous if not already at first slide
                if (currentIndex > 0) {
                  goToSlide(currentIndex - 1);
                }
              });
            }

            if (nextButton) {
              nextButton.addEventListener('click', () => {
                // Only go to next if not already at last slide
                if (currentIndex < slides.length - 1) {
                  goToSlide(currentIndex + 1);
                }
              });
            }
          }

          // Add resize event listener for orientation changes
          const handleResize = () => {
            goToSlide(currentIndex);
          };

          // Debounce function for resize events
          const debounce = (func, wait) => {
            let timeout;
            return () => {
              clearTimeout(timeout);
              timeout = setTimeout(func, wait);
            };
          };

          // Add event listener with debounce
          window.addEventListener('resize', debounce(handleResize, 200));

          // Setup MutationObserver to maintain our dot states
          // This helps if other scripts or the component itself tries to override our settings
          const dotsMutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (
                mutation.type === 'attributes' &&
                (mutation.attributeName === 'aria-current' || mutation.attributeName === 'class')
              ) {
                const targetDot = mutation.target;
                // Find the index of this dot
                const dotContainer = slider.querySelector('[data-role="navigation"]');
                if (dotContainer) {
                  const allDots = Array.from(dotContainer.querySelectorAll('.mx-bullet'));
                  const dotIndex = allDots.indexOf(targetDot);

                  if (dotIndex !== -1) {
                    // If this dot's state doesn't match what it should be based on currentIndex,
                    // update it again to maintain our desired state
                    const shouldBeActive = dotIndex === currentIndex;
                    const isActiveByAttr = targetDot.getAttribute('aria-current') === 'true';

                    if (shouldBeActive !== isActiveByAttr) {
                      if (shouldBeActive) {
                        targetDot.setAttribute('aria-current', 'true');
                        targetDot.style.cssText = 'background-color: #19435a !important; opacity: 1 !important;';
                        // Add active classes
                        targetDot.classList.add('active');
                        targetDot.classList.add('active-dot');
                        targetDot.classList.add('mx-bullet--active');
                      } else {
                        targetDot.setAttribute('aria-current', 'false');
                        targetDot.style.cssText = '';
                        // Remove active classes
                        targetDot.classList.remove('active');
                        targetDot.classList.remove('active-dot');
                        targetDot.classList.remove('mx-bullet--active');
                      }
                    }
                  }
                }
              }
            });
          });

          // Start observing dots for attribute and class changes
          const dotsContainer = slider.querySelector('[data-role="navigation"]');
          if (dotsContainer) {
            const allDots = dotsContainer.querySelectorAll('.mx-bullet');
            allDots.forEach((dot) => {
              dotsMutationObserver.observe(dot, {
                attributes: true,
                attributeFilter: ['aria-current', 'class'],
              });
            });
          }
        }
      }
    } else {
      // This might happen if the fetched template structure changes unexpectedly
      console.warn(
        `RelatedProducts (${this.sectionId}): Could not find mx-slider or .empty-recommendations in response. Displaying raw response as fallback.`,
      );
      this.container.innerHTML = htmlContent; // Fallback: insert raw content
    }

    // Optional: Dispatch an event if other components need to know the slider was loaded
    this.container.dispatchEvent(new CustomEvent('related-products:slider-loaded', { bubbles: true }));
  }

  showError(message = 'Error loading recommendations') {
    this.container.innerHTML = `<div class="empty-recommendations"><p>${message}</p></div>`;
  }

  showEmpty(message = 'No related products found.') {
    this.container.innerHTML = `<div class="empty-recommendations"><p>${message}</p></div>`;
  }

  // Initialize drag functionality
  initializeDragBehavior(slider, sliderTrack) {
    if (!slider || !sliderTrack) return;

    // Get access to the existing variables
    const slides = Array.from(sliderTrack.children);

    // Reference to the goToSlide function
    const goToSlide = (index) => {
      const validIndex = Math.max(0, Math.min(index, slides.length - 1));

      // Calculate position based on viewport width
      let position;
      if (window.innerWidth < 640) {
        // For mobile, ensure we're not skipping slides
        const mobileSlideWidth = 85;
        position = validIndex * mobileSlideWidth;
      } else {
        position = validIndex * (100 / slides.length);
      }

      // Ensure smooth transition is applied for navigation
      sliderTrack.style.transition = 'transform 0.5s ease';

      // Apply the transform
      sliderTrack.style.transform = `translateX(-${position}%)`;

      // Update nav dots if they exist
      const navDots = slider.querySelectorAll('[data-role="navigation"] [aria-current]');
      if (navDots && navDots.length) {
        navDots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === validIndex ? 'true' : 'false');
        });
      }
    };

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let startTranslateX = 0;
    let currentTranslateX = 0;
    let startTime = 0;
    let preventClick = false;

    // Function to get current translateX value
    const getTranslateX = () => {
      const transform = window.getComputedStyle(sliderTrack).getPropertyValue('transform');
      if (transform === 'none' || !transform) return 0;

      const matrix = transform.match(/^matrix\((.+)\)$/);
      if (!matrix || matrix.length < 2) return 0;

      const values = matrix[1].split(', ');
      return parseFloat(values[4]) || 0;
    };

    // Add event listeners for drag
    sliderTrack.addEventListener('mousedown', startDrag);
    sliderTrack.addEventListener('touchstart', startDrag, { passive: true });

    // Function to start drag
    function startDrag(e) {
      isDragging = true;

      // Add visual feedback
      sliderTrack.classList.add('dragging');

      // Get the correct client coordinates for both mouse and touch events
      const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

      startX = clientX;
      startY = clientY;

      // Get current transform value
      startTranslateX = getTranslateX();
      currentTranslateX = startTranslateX;

      startTime = Date.now();

      preventClick = false;

      // Use passive: true for touch events to improve performance
      document.addEventListener('mousemove', drag);
      document.addEventListener('touchmove', drag, { passive: true });
      document.addEventListener('mouseup', endDrag);
      document.addEventListener('touchend', endDrag);

      // Add temporary click handler to prevent clicks during drag
      sliderTrack.addEventListener('click', preventClickHandler, true);

      // Prevent default to avoid text selection during drag on mouse only
      // For touch, we want passive behavior for better performance
      if (e.type === 'mousedown') {
        e.preventDefault();
      }
    }

    // Function to handle drag movement
    function drag(e) {
      if (!isDragging) return;

      // Only prevent default for mouse events
      if (e.type === 'mousemove') {
        e.preventDefault();
      }

      // Get correct client coordinates based on event type
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

      const dx = clientX - startX;
      const dy = clientY - startY;

      // Set click prevention if movement exceeds threshold
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        preventClick = true;
      }

      // For better mobile performance, only update the transform if movement is significant
      if (Math.abs(dx) > 2) {
        // Calculate the new translate value
        currentTranslateX = startTranslateX + dx;

        // Apply the transform directly
        sliderTrack.style.transition = 'none';
        sliderTrack.style.transform = `translateX(${currentTranslateX}px)`;
      }
    }

    // Function to end drag
    function endDrag(e) {
      if (!isDragging) return;

      isDragging = false;

      // Remove visual feedback
      sliderTrack.classList.remove('dragging');

      // Get correct client coordinates for end position
      const clientX = e.type === 'touchend' ? e.changedTouches[0].clientX : e.clientX;

      const dx = clientX - startX;
      const elapsed = Date.now() - startTime;
      const velocity = Math.abs(dx) / elapsed;

      // Calculate current slide based on current index - fix the index retrieval
      const currentDot = slider.querySelector('[data-role="navigation"] [aria-current="true"]');
      let targetIndex = 0;

      // Find the index of the current dot
      if (currentDot) {
        const dots = Array.from(slider.querySelectorAll('[data-role="navigation"] .mx-bullet'));
        targetIndex = dots.indexOf(currentDot);
      }

      // Determine target slide based on swipe direction and velocity
      const significantSwipe = Math.abs(dx) > 50 || velocity > 0.5;

      if (significantSwipe) {
        if (dx > 0) {
          // Swiped right - go to previous slide
          targetIndex = Math.max(0, targetIndex - 1);
        } else if (dx < 0) {
          // Swiped left - go to next slide
          targetIndex = Math.min(slides.length - 1, targetIndex + 1);
        }
      }

      // Restore transition for smooth animation
      sliderTrack.style.transition = 'transform 0.5s ease';

      // Go to the target slide
      goToSlide(targetIndex);

      // Clean up event listeners
      document.removeEventListener('mousemove', drag);
      document.removeEventListener('touchmove', drag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchend', endDrag);

      // Remove click prevention after a short delay
      setTimeout(() => {
        sliderTrack.removeEventListener('click', preventClickHandler, true);
      }, 100);
    }

    // Function to prevent clicks during drag
    function preventClickHandler(e) {
      if (preventClick) {
        e.preventDefault();
        e.stopPropagation();
        preventClick = false;
      }
    }

    // Add the drag functionality to the slider
    sliderTrack.classList.add('draggable');
  }
}

// Initialize all related product sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const recommendationContainers = document.querySelectorAll('.product-recommendations');

  recommendationContainers.forEach((container) => {
    new RelatedProducts(container);
  });
});

// Handle Shopify Theme Editor events
document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail.sectionId;
  const container = event.target.querySelector(`.product-recommendations[data-section-id="${sectionId}"]`);
  if (container) {
    new RelatedProducts(container);
  }
});
